import { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Spinner } from '../components/ui/spinner';
import { MapLocationButton } from '../components/MapLocationButton';
import { Heart, MapPin, Phone, Mail, Globe, ExternalLink, Search } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const healthIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 100 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:#ef4444;">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

// Default filter - only load "Centro De Salud" initially
const DEFAULT_HEALTH_FILTER = 'Centro De Salud';

export function ServicesPage() {
  const { language } = useLanguage();
  const [healthData, setHealthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState(DEFAULT_HEALTH_FILTER);
  const [loadedTypes, setLoadedTypes] = useState(new Set([DEFAULT_HEALTH_FILTER]));

  const t = {
    title: language === 'es' ? 'Atención Sanitaria' : 'Healthcare',
    subtitle: language === 'es' ? 'Centros médicos, farmacias y servicios sanitarios' : 'Medical centers, pharmacies and health services',
    searchPlaceholder: language === 'es' ? 'Buscar por nombre o municipio...' : 'Search by name or municipality...',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    results: language === 'es' ? 'resultados' : 'results',
    allTypes: language === 'es' ? 'Todos' : 'All',
    filterByType: language === 'es' ? 'Filtrar por tipo' : 'Filter by type',
    openInGoogleMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
    loading: language === 'es' ? 'Cargando datos...' : 'Loading data...',
    centers: language === 'es' ? 'centros' : 'centers',
  };

  // Color palette for health types
  const typeColors = {
    'Farmacia': '#22c55e',
    'Servicios Asistenciales': '#ef4444',
    'Clinica Dental': '#8b5cf6',
    'Fisioterapia Rehabilitacion': '#f97316',
    'Centro De Salud': '#3b82f6',
    'Servicios Veterinarios': '#84cc16',
    'Servicios Profesionales Medicos': '#ec4899',
    'Servicios Hospitalarios': '#dc2626',
    'Optica': '#06b6d4',
    'Servicios Profesionales Psicologia': '#a855f7',
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/services/health`);
        setHealthData(response.data || []);
      } catch (error) {
        console.error('Error fetching health data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get unique types for filter
  const healthTypes = useMemo(() => {
    const types = new Set();
    healthData.forEach(item => {
      if (item.tipo) types.add(item.tipo);
    });
    return Array.from(types).sort();
  }, [healthData]);

  // Filter data - only show loaded types
  const filteredData = useMemo(() => {
    let filtered = healthData.filter(item => loadedTypes.has(item.tipo));
    
    if (healthFilter !== 'all') {
      filtered = filtered.filter(item => item.tipo === healthFilter);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.nombre || '').toLowerCase().includes(searchLower) ||
        (item.municipio || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [healthData, healthFilter, search, loadedTypes]);

  // Handle filter change - load type data when selected
  const handleFilterChange = (tipo) => {
    if (tipo !== 'all' && !loadedTypes.has(tipo)) {
      setLoadedTypes(prev => new Set([...prev, tipo]));
    }
    setHealthFilter(tipo);
  };

  // Handle "all" filter - load all types
  const handleShowAll = () => {
    const allTypes = new Set(healthData.map(item => item.tipo).filter(Boolean));
    setLoadedTypes(allTypes);
    setHealthFilter('all');
  };

  const markers = useMemo(() => 
    filteredData.filter(item => item.lat && item.lon),
    [filteredData]
  );

  const centerPosition = [28.2916, -16.5291];

  const openInGoogleMaps = (lat, lon) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`, '_blank');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="w-5 h-5 text-red-500" />
          <span className="text-sm font-medium text-red-500 uppercase tracking-wider">
            {language === 'es' ? 'Datos Abiertos' : 'Open Data'}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2" data-testid="services-title">
          {t.title}
        </h1>
        <p className="text-lg text-[#252a34]/70">{t.subtitle}</p>
      </div>

      {/* Stats */}
      <div className="mb-6 flex items-center gap-4">
        <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200">
          <span className="text-2xl font-bold text-red-500">{healthData.length.toLocaleString()}</span>
          <span className="text-gray-600 ml-2">{t.centers}</span>
        </div>
      </div>

      {/* Type Filters */}
      {!loading && (
        <div className="mb-4">
          <p className="text-sm text-[#252a34]/60 mb-2">{t.filterByType}:</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleShowAll}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                healthFilter === 'all'
                  ? 'bg-red-500 text-white'
                  : 'bg-white text-[#252a34] border border-slate-200 hover:border-red-400'
              }`}
            >
              {t.allTypes} ({healthData.length.toLocaleString()})
            </button>
            {healthTypes.map((tipo) => {
              const count = healthData.filter(h => h.tipo === tipo).length;
              const color = typeColors[tipo] || '#6b7280';
              const isActive = healthFilter === tipo;
              const isLoaded = loadedTypes.has(tipo);
              return (
                <button
                  key={tipo}
                  onClick={() => handleFilterChange(tipo)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    isActive
                      ? 'text-white shadow-md'
                      : isLoaded 
                        ? 'bg-white text-[#252a34] border border-slate-200 hover:shadow-sm'
                        : 'bg-slate-100 text-[#252a34]/70 border border-dashed border-slate-300 hover:border-slate-400'
                  }`}
                  style={isActive ? { backgroundColor: color } : {}}
                >
                  {tipo} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      {!loading && (
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
            <Input
              type="text"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-white border-slate-200"
              data-testid="services-search"
            />
          </div>
          <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
            {t.showing} <strong className="text-red-500">{filteredData.length.toLocaleString()}</strong> {t.of} {healthData.length.toLocaleString()} {t.results}
          </div>
        </div>
      )}

      {/* Map */}
      <Card className="overflow-hidden border-slate-200 mb-6" data-testid="services-map">
        <div className="h-[400px] md:h-[450px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full bg-slate-50">
              <Spinner size="lg" />
              <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
            </div>
          ) : (
            <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapLocationButton language={language} />
              <MarkerClusterGroup 
                chunkedLoading 
                iconCreateFunction={createClusterIcon} 
                maxClusterRadius={50}
              >
                {markers.map((item, idx) => (
                  <Marker 
                    key={idx} 
                    position={[item.lat, item.lon]} 
                    icon={healthIcon}
                  >
                    <Popup>
                      <div className="font-['Outfit'] min-w-[220px] max-w-[280px]">
                        <h3 className="font-bold text-[#252a34] mb-1 text-sm">{item.nombre}</h3>
                        <Badge 
                          className="text-xs mb-2 text-white"
                          style={{ backgroundColor: typeColors[item.tipo] || '#6b7280' }}
                        >
                          {item.tipo}
                        </Badge>
                        <div className="text-xs text-[#252a34]/70 space-y-1 mt-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span>{item.municipio}</span>
                          </div>
                          {item.direccion && (
                            <p className="text-[#252a34]/60 ml-4">{item.direccion}</p>
                          )}
                          {item.telefono && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3 flex-shrink-0" />
                              <a href={`tel:${item.telefono}`} className="text-[#00599c] hover:underline">
                                {item.telefono}
                              </a>
                            </div>
                          )}
                          {item.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{item.email}</span>
                            </div>
                          )}
                          {item.web && (
                            <div className="flex items-center gap-1">
                              <Globe className="w-3 h-3 flex-shrink-0" />
                              <a 
                                href={item.web.startsWith('http') ? item.web : `https://${item.web}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#00599c] hover:underline truncate"
                              >
                                Web
                              </a>
                            </div>
                          )}
                        </div>
                        <button 
                          onClick={() => openInGoogleMaps(item.lat, item.lon)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-3"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {t.openInGoogleMaps}
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          )}
        </div>
      </Card>
    </div>
  );
}
